/**
 * Picks up a new build of the web site without asking the user.
 *
 * The site is served through a service worker. After a deploy the browser
 * downloads the new build in the background and holds it "waiting" until it is
 * told to switch. This used to show a full-width blue "A new version is
 * available. Refresh Now / Later" bar on every open tab within five minutes of
 * every deploy. Users could not tell why they had to do it, and they did not:
 * the old build kept working.
 *
 * Now the switch happens at a moment the user would not notice, and only when
 * nothing would be lost (no open dialog, no unsaved work, not typing: see
 * utils/updateMoment.ts):
 * - in the main site, on the next move to a different section (Residents to
 *   Reports, say). The page they left is unmounted anyway, and its own
 *   unsaved-changes guard has already run, so loading the new build there loses
 *   nothing. Moves inside a section (/selective-touch/patient to /review) do not
 *   count: a section can keep work in progress across its own sub-pages.
 *   The reload lands on the page they were going to. See AppUpdateOnNavigation.
 * - on coming back to a tab that was hidden for a while, if the page has not
 *   been edited. See AppUpdateOnNavigation.
 * - on kiosk screens, when they sit idle (the tablet PIN pad, the bedside
 *   screensaver). See useApplyUpdateWhenIdle.
 * - someone who stays on one screen all day gets a quiet "Update ready" entry
 *   in the user menu once the update has waited UPDATE_NUDGE_AFTER_MS.
 *
 * The reload itself is made to look like a page change: the page area empties,
 * and the new build starts under a copy of the rail and header, so there is no
 * white page or splash screen in between. See utils/updateShell.ts.
 *
 * Registration is done here rather than through vite-plugin-pwa's
 * registerSW: its prompt mode reloads EVERY open tab when one tab applies the
 * update, which would throw away a form in progress in another tab. Here only
 * the tab that applies it reloads; the others stay on the old build and switch
 * at their own next section change.
 */
import { useEffect, useState, useSyncExternalStore } from 'react';
import { beginUpdateTransition, endUpdateTransition, saveShellSnapshot } from './updateShell';
import { updateBlocker, type UpdateMoment } from './updateMoment';

/** How often an open tab asks the server whether a new build is out. */
export const UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;
/** After this long waiting, the user menu offers the update. */
export const UPDATE_NUDGE_AFTER_MS = 8 * 60 * 60 * 1000;
/**
 * If the new worker has not taken over by then, give up on it: unregister and
 * reload from the server (see applyUpdate). Activation normally takes well
 * under a second; it waits only while the old worker finishes requests in
 * flight, or while this page's main thread is busy rendering the section the
 * user just opened, which delays the controllerchange event.
 *
 * Was 10 s. Since the page area now empties while the switch runs (so the user
 * waits on a spinner instead of using the old page), a takeover that stalls
 * goes to the fallback sooner: in testing a stalled one never completed in the
 * 10 s anyway, while a normal one took about 30 ms. The timer re-checks the
 * worker before giving up, so a takeover that happened behind a busy main
 * thread still counts.
 */
export const TAKEOVER_TIMEOUT_MS = 3_000;
/**
 * A worker that has started activating by TAKEOVER_TIMEOUT_MS is on its way:
 * it gets until this long (from the start) before the fallback.
 */
export const ACTIVATING_TIMEOUT_MS = 10_000;
/** The fallback reloads after this long even if unregister() never settles. */
export const UNREGISTER_WAIT_MS = 2_000;
/**
 * A reload that has not unloaded the page by then was cancelled (the browser
 * asked "Leave site?" and the user stayed): show the page again.
 */
export const RELOAD_CANCELLED_AFTER_MS = 4_000;

type Listener = () => void;

let pendingSince: number | null = null;
let registration: ServiceWorkerRegistration | null = null;
let applying = false;
/**
 * The real reload: saves the copy of the frame the next page paints first
 * (utils/updateShell.ts), then reloads the same address.
 */
function reloadIntoNewBuild() {
  saveShellSnapshot();
  window.location.reload();
  setTimeout(() => {
    applying = false;
    endUpdateTransition();
  }, RELOAD_CANCELLED_AFTER_MS);
}

let reloadPage: () => void = reloadIntoNewBuild;
const listeners = new Set<Listener>();

function emit() {
  for (const l of listeners) l();
}

/** Record that a new build is ready. Exported for tests. */
export function markUpdatePending(now: number = Date.now()) {
  if (pendingSince !== null) return;
  pendingSince = now;
  emit();
}

export function isUpdatePending(): boolean {
  return pendingSince !== null;
}

export function getUpdatePendingSince(): number | null {
  return pendingSince;
}

function subscribe(listener: Listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** When the waiting update was first seen in this tab, or null when there is none. */
export function useUpdatePendingSince(): number | null {
  return useSyncExternalStore(subscribe, getUpdatePendingSince, () => null);
}

/**
 * Switch this tab to the new build: tell the waiting worker to take over,
 * then reload once it has. With no waiting worker (another tab already
 * switched it), the reload alone loads the new build.
 *
 * The reload must wait for the takeover. Until the new worker is active the
 * OLD one answers the reload, from its precache, with the OLD index.html. If
 * the new worker then activates before that page has fetched its scripts, its
 * activation deletes the old build's files from the precache, the deploy has
 * already removed them from the server, and the old page's main script is a
 * 404: a spinner for ever (2026-10-21, /reports/run/special-devices; the boot
 * watchdog in index.html now recovers from that, but this should not cause it).
 * This used to reload after 3 s whether or not the new worker had taken over.
 *
 * So the reload happens on 'controllerchange' (the new worker controls this
 * page; a navigation it receives while still activating waits until it is
 * activated, so it can only serve the new build), or when the new worker
 * reaches 'activated' (covers a page the worker does not control, such as one
 * opened with Ctrl+F5, which never gets controllerchange).
 *
 * If neither happens within TAKEOVER_TIMEOUT_MS, a plain reload is not safe:
 * the old worker might answer it with the old shell and the new one activate
 * mid-load, the same race. A cache-busting URL does not help either, since the
 * worker answers every navigation from its precache whatever the query string,
 * and a page cannot ask for a reload that bypasses the worker. So the fallback
 * unregisters the registration first, which takes it out of use for new
 * navigations at once: the reload comes from the server, gets the new build,
 * and the page registers the worker again (it precaches afresh, a one-off
 * cost). Other tabs keep the worker they have until they next switch.
 */
export function applyUpdate(reload: () => void = reloadPage) {
  if (applying || pendingSince === null) return;
  applying = true;
  beginUpdateTransition();
  const reg = registration;
  const waiting = reg?.waiting;
  if (!reg || !waiting || typeof navigator === 'undefined' || !navigator.serviceWorker) {
    reload();
    return;
  }
  const container = navigator.serviceWorker;
  let done = false;
  const cleanUp = () => {
    container.removeEventListener('controllerchange', go);
    waiting.removeEventListener('statechange', onStateChange);
    clearTimeout(fallbackTimer);
  };
  function go() {
    if (done) return;
    done = true;
    cleanUp();
    reload();
  }
  const onStateChange = () => {
    if (waiting.state === 'activated') go();
  };
  const startedAt = Date.now();
  const onTimeout = () => {
    if (done) return;
    // It may have taken over with the event still queued behind a busy main thread.
    if (container.controller === waiting || waiting.state === 'activated') {
      go();
      return;
    }
    // Activating: on its way, give it the rest of ACTIVATING_TIMEOUT_MS.
    const left = ACTIVATING_TIMEOUT_MS - (Date.now() - startedAt);
    if (waiting.state === 'activating' && left > 0) {
      fallbackTimer = setTimeout(onTimeout, left);
      return;
    }
    done = true;
    cleanUp();
    let reloaded = false;
    const reloadOnce = () => {
      if (reloaded) return;
      reloaded = true;
      reload();
    };
    setTimeout(reloadOnce, UNREGISTER_WAIT_MS);
    Promise.resolve()
      .then(() => reg.unregister())
      .then(reloadOnce, reloadOnce);
  };
  let fallbackTimer = setTimeout(onTimeout, TAKEOVER_TIMEOUT_MS);
  container.addEventListener('controllerchange', go);
  waiting.addEventListener('statechange', onStateChange);
  waiting.postMessage({ type: 'SKIP_WAITING' });
}

/** Apply the update now if one is waiting. Returns whether it started. */
export function applyUpdateIfPending(reload?: () => void): boolean {
  if (pendingSince === null || applying) return false;
  applyUpdate(reload);
  return true;
}

/**
 * Apply a waiting update if this is a safe moment for it (utils/updateMoment.ts).
 * Returns whether it started.
 */
export function applyUpdateIfSafe(moment: UpdateMoment, pathname?: string): boolean {
  if (pendingSince === null || applying) return false;
  if (updateBlocker(moment, { pathname }) !== null) return false;
  applyUpdate();
  return true;
}

/**
 * Load the new build because this tab's copy of the old one is broken (a page
 * file the deploy removed, utils/staleBuild.ts). With a new worker waiting it
 * takes over first, as applyUpdate does; otherwise this is a reload that still
 * looks like a page change. Returns false when a switch is already under way.
 */
export function loadNewBuildNow(reload: () => void = reloadPage): boolean {
  if (applying) return false;
  if (pendingSince === null) markUpdatePending();
  applyUpdate(reload);
  return true;
}

/** Ask the server for a newer build now (a tab coming back after a while). */
export function checkForUpdate() {
  registration?.update().catch(() => {
    /* offline or the server is mid-deploy: the interval tries again */
  });
}

/**
 * The first path segment: the section of the site the user is in.
 * "/residents/12/diet" -> "residents", "/" -> "".
 */
export function sectionOf(pathname: string): string {
  return pathname.split('/').filter(Boolean)[0]?.toLowerCase() ?? '';
}

/** True once a waiting update is older than UPDATE_NUDGE_AFTER_MS (the user menu offers it then). */
export function useUpdateNudge(): boolean {
  const since = useUpdatePendingSince();
  // The `since` the nudge came due for; a newer update starts the wait again.
  const [dueFor, setDueFor] = useState<number | null>(null);
  useEffect(() => {
    if (since === null) return;
    const wait = Math.max(0, since + UPDATE_NUDGE_AFTER_MS - Date.now());
    const t = setTimeout(() => setDueFor(since), wait);
    return () => clearTimeout(t);
  }, [since]);
  return since !== null && dueFor === since;
}

/**
 * Kiosk screens: apply a waiting update while `idle` is true (a PIN pad with
 * nothing typed, a screensaver). Runs on mount and whenever an update arrives.
 */
export function useApplyUpdateWhenIdle(idle: boolean) {
  const pending = useUpdatePendingSince();
  useEffect(() => {
    if (idle && pending !== null) applyUpdateIfPending();
  }, [idle, pending]);
}

function watch(reg: ServiceWorkerRegistration) {
  registration = reg;
  // Without a controller this is the first visit: the worker installing now is
  // the first one, not an update.
  if (reg.waiting && navigator.serviceWorker.controller) markUpdatePending();
  reg.addEventListener('updatefound', () => {
    const sw = reg.installing;
    if (!sw) return;
    sw.addEventListener('statechange', () => {
      if (sw.state === 'installed' && navigator.serviceWorker.controller) markUpdatePending();
    });
  });
}

/**
 * The API response cache earlier service workers kept (see vite.config.ts: it is no longer
 * written or read). Deleted so the responses it holds, keyed by URL only and so not tied to a
 * connection or user, do not sit in the browser.
 */
export const RETIRED_API_CACHE = 'api-cache';

export function deleteRetiredApiCache(): void {
  try {
    if (typeof caches === 'undefined') return;
    void caches.delete(RETIRED_API_CACHE).catch(() => {
      /* storage blocked: nothing we can do, and nothing reads it any more */
    });
  } catch {
    /* caches unavailable (insecure context, locked-down browser) */
  }
}

/** Register the service worker. Production builds only; dev has none. */
export function startAppUpdates() {
  if (!import.meta.env.PROD || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  deleteRetiredApiCache();

  const hadController = !!navigator.serviceWorker.controller;
  // Another tab switched to the new build: this one is now running old code
  // under the new worker. It switches at its own next section change.
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (hadController && !applying) markUpdatePending();
  });

  navigator.serviceWorker
    .register('/sw.js', { scope: '/' })
    .then((reg) => {
      watch(reg);
      setInterval(() => {
        reg.update().catch(() => {
          /* offline or the server is mid-deploy: try again next time */
        });
      }, UPDATE_CHECK_INTERVAL_MS);
    })
    .catch((err: unknown) => {
      console.error('Service worker registration failed:', err);
    });
}

/** Tests only: forget everything; `reload` stands in for window.location.reload. */
export function resetAppUpdateForTests(reload: () => void = () => {}, reg: ServiceWorkerRegistration | null = null) {
  pendingSince = null;
  registration = reg;
  applying = false;
  reloadPage = reload;
  listeners.clear();
}
